// Note: for some reason i18n.exists() is not working with jest, it always returns false
// So expect to always see the translation key returned in unit tests
import i18next from "i18next";
import { z, ZodIssueCode } from "zod";

import validationDe from "~public/locales/de/validation/general.json";
import validationEn from "~public/locales/en/validation/general.json";
import validationEs from "~public/locales/es/validation/general.json";
import validationFr from "~public/locales/fr/validation/general.json";
import validationNl from "~public/locales/nl/validation/general.json";
import validationPt from "~public/locales/pt/validation/general.json";

const TRANSLATION_NAMESPACE = "validation";

const translationMaps = {
  de: validationDe,
  en: validationEn,
  nl: validationNl,
  pt: validationPt,
  es: validationEs,
  fr: validationFr,
};

// create instance specifically for zod validation so we can use the same
// translation formatting as in the rest of the website
const i18n = i18next.createInstance({
  initAsync: false,
  resources: translationMaps,
  ns: [TRANSLATION_NAMESPACE],
  defaultNS: TRANSLATION_NAMESPACE,
  fallbackLng: "nl",
  lng: "nl",
});
i18n.init({
  debug: false,
  interpolation: {
    escapeValue: false, // React already does escaping
  },
  returnObjects: true,
});

/**
 * @param {import("~types/types").AppLocale} locale
 * @param {Record<string, string>} customTranslations
 * @returns {(error: z.ZodIssueOptionalMessage, ctx: z.ErrorMapCtx) => ({ message: string})}
 */
function getLocaleErrorMap(locale, customTranslations = {}) {
  const translations = {
    ...translationMaps[locale],
    ...customTranslations,
  };

  i18n.addResourceBundle(
    locale,
    TRANSLATION_NAMESPACE,
    translations,
    true,
    true,
  );

  /**
   * @param {z.ZodIssueOptionalMessage} error
   * @param {z.ErrorMapCtx} ctx
   * @returns {{ message: string }}
   */
  return (error, ctx) => {
    const t = i18n.getFixedT(locale, TRANSLATION_NAMESPACE);
    /** @type {string} */
    let translationKey = error.code;

    switch (error.code) {
      case ZodIssueCode.invalid_type:
        translationKey = `${error.code}.${error.expected}`;
        break;

      case ZodIssueCode.invalid_string:
        translationKey = `${error.code}.${error.validation}`;
        break;

      case ZodIssueCode.too_small:
      case ZodIssueCode.too_big:
        translationKey = `${error.code}.${error.type}`;
        break;

      case ZodIssueCode.custom:
        translationKey = `${error.code}.${error.params?.key || "default"}`;
        break;
    }

    if (!error.path?.length) {
      // Use fallback or show the key as-is to identify missing translations
      return {
        message: i18n.exists(`${TRANSLATION_NAMESPACE}:${translationKey}`)
          ? t(translationKey, { ...error, ...ctx })
          : translationKey,
      };
    }

    // Check if the error path has a specific translation
    const fullKey = `${error.path.join(".")}.${translationKey}`;
    if (i18n.exists(`${TRANSLATION_NAMESPACE}:${fullKey}`)) {
      return {
        message: t(fullKey, { ...error, ...ctx }),
      };
    }
    if (i18n.exists(`${TRANSLATION_NAMESPACE}:${translationKey}`)) {
      return {
        message: t(translationKey, { ...error, ...ctx }),
      };
    }
    return {
      message: `${fullKey} | ${translationKey}`,
    };
  };
}

/**
 * @param {import("~types/types").AppLocale} locale
 * @param {Record<string, string>} customErrorMap
 */
export function setZodLocale(locale, customErrorMap = {}) {
  // load the right locale for zod
  z.setErrorMap(getLocaleErrorMap(locale, customErrorMap));
}
